import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { UserOut, UserCreate, UserUpdate, UserRole, Gender, EventOut } from '@/types/api';
import { isSuperAdmin } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import Loader from '@/components/atoms/Loader/Loader';
import Icon from '@/components/atoms/Icon/Icon';
import './UserManagement.scss';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState<UserOut[]>([]);
  const [events, setEvents] = useState<EventOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<UserOut | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<UserCreate>>({
    email: '',
    username: '',
    role: 'customer',
    event_id: null,
    date_of_birth: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    gender: null,
    password: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Check permissions
  if (!isSuperAdmin(user)) {
    return (
      <div className="user-management-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <p>{t('errors.unauthorized')}</p>
          </Card>
        </div>
      </div>
    );
  }

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: { role?: string } = {};
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }
      const usersData = await apiService.getUsers(params);
      setUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [roleFilter, t]);

  const fetchEvents = useCallback(async () => {
    try {
      const eventsData = await apiService.getEvents();
      setEvents(eventsData);
    } catch (err: any) {
      console.error('Error fetching events:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchEvents();
  }, [fetchUsers, fetchEvents]);

  const handleInputChange = (field: keyof UserCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('admin.users.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('admin.users.errors.emailInvalid');
    }

    if (!formData.username) {
      errors.username = t('admin.users.errors.usernameRequired');
    }

    if (!formData.role) {
      errors.role = t('admin.users.errors.roleRequired');
    }

    if ((formData.role === 'barman' || formData.role === 'warehouse') && !formData.event_id) {
      errors.event_id = t('admin.users.errors.eventRequired');
    }

    if (!formData.date_of_birth) {
      errors.date_of_birth = t('admin.users.errors.dateOfBirthRequired');
    }

    if (!editingUser && !formData.password) {
      errors.password = t('admin.users.errors.passwordRequired');
    } else if (formData.password && formData.password.length < 8) {
      errors.password = t('admin.users.errors.passwordMinLength');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = (user: UserOut) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      role: user.role,
      event_id: user.event_id,
      date_of_birth: user.date_of_birth,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      address: user.address || '',
      gender: user.gender,
      password: '' // Don't pre-fill password
    });
    setShowAddForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setEditingUser(null);
    setShowAddForm(false);
    setFormData({
      email: '',
      username: '',
      role: 'customer',
      event_id: null,
      date_of_birth: '',
      first_name: '',
      last_name: '',
      phone: '',
      address: '',
      gender: null,
      password: ''
    });
    setFormErrors({});
    setError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (editingUser) {
        // Update existing user
        const updateData: UserUpdate = {
          email: formData.email || null,
          username: formData.username || null,
          role: formData.role || null,
          event_id: formData.event_id || null,
          date_of_birth: formData.date_of_birth || null,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          phone: formData.phone || null,
          address: formData.address || null,
          gender: formData.gender || null
        };
        await apiService.updateUser(editingUser.id, updateData);
        
        // Update password if provided
        if (formData.password) {
          await apiService.resetUserPassword(editingUser.id, formData.password);
        }
      } else {
        // Create new user
        const createData: UserCreate = {
          email: formData.email!,
          username: formData.username!,
          role: formData.role!,
          event_id: formData.event_id || null,
          date_of_birth: formData.date_of_birth!,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          phone: formData.phone || null,
          address: formData.address || null,
          gender: formData.gender || null,
          password: formData.password!
        };
        await apiService.createUser(createData);
      }

      await fetchUsers();
      handleCancel();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm(t('admin.users.deleteConfirm'))) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="user-management-page">
        <div className="container">
          <Loader fullScreen size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <div className="container">
        <div className="user-management-page__header">
          <h1 className="user-management-page__title">
            {t('admin.users.title')}
          </h1>
          <p className="user-management-page__description">
            {t('admin.users.description')}
          </p>
        </div>

        {error && (
          <Card variant="elevated" padding="md" className="user-management-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Search and Filters */}
        <Card variant="elevated" padding="lg" className="user-management-page__filters">
          <div className="user-management-page__filters-content">
            <FormField
              type="text"
              name="search"
              label={t('admin.users.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.users.searchPlaceholder')}
            />
            <FormField
              type="select"
              name="role"
              label={t('admin.users.filterByRole')}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">{t('admin.users.allRoles')}</option>
              <option value="super_admin">{t('admin.users.roles.super_admin')}</option>
              <option value="organizer">{t('admin.users.roles.organizer')}</option>
              <option value="barman">{t('admin.users.roles.bartender')}</option>
              <option value="warehouse">{t('admin.users.roles.warehouse')}</option>
              <option value="customer">{t('admin.users.roles.customer')}</option>
            </FormField>
            <div className="user-management-page__actions">
              <Button
                variant="primary"
                onClick={() => {
                  handleCancel();
                  setShowAddForm(true);
                }}
              >
                <Icon name="mas" size={16} /> {t('admin.users.addUser')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card variant="elevated" padding="lg" className="user-management-page__form-card">
            <h3>{editingUser ? t('admin.users.editUser') : t('admin.users.addUser')}</h3>
            <form onSubmit={handleSave}>
              <div className="user-management-page__form-grid">
                <FormField
                  type="email"
                  name="email"
                  label={t('admin.users.email')}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={formErrors.email}
                  required
                  disabled={isSubmitting}
                />
                <FormField
                  type="text"
                  name="username"
                  label={t('admin.users.username')}
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  error={formErrors.username}
                  required
                  disabled={isSubmitting}
                />
                <FormField
                  type="select"
                  name="role"
                  label={t('admin.users.role')}
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                  error={formErrors.role}
                  required
                  disabled={isSubmitting}
                >
                  <option value="customer">{t('admin.users.roles.customer')}</option>
                  <option value="barman">{t('admin.users.roles.bartender')}</option>
                  <option value="warehouse">{t('admin.users.roles.warehouse')}</option>
                  <option value="organizer">{t('admin.users.roles.organizer')}</option>
                  <option value="super_admin">{t('admin.users.roles.super_admin')}</option>
                </FormField>
                <SearchableSelect<number>
                  name="event_id"
                  label={t('admin.users.event')}
                  placeholder={t('admin.users.noEvent')}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={formData.event_id || null}
                  onChange={(value) => handleInputChange('event_id', value)}
                  error={formErrors.event_id}
                  disabled={isSubmitting}
                />
                <FormField
                  type="date"
                  name="date_of_birth"
                  label={t('admin.users.dateOfBirth')}
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  error={formErrors.date_of_birth}
                  required
                  disabled={isSubmitting}
                />
                <FormField
                  type="select"
                  name="gender"
                  label={t('admin.users.gender')}
                  value={formData.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value || null)}
                  disabled={isSubmitting}
                >
                  <option value="">{t('admin.users.noGender')}</option>
                  <option value="man">{t('admin.users.genders.man')}</option>
                  <option value="woman">{t('admin.users.genders.woman')}</option>
                  <option value="nonbinary">{t('admin.users.genders.nonbinary')}</option>
                </FormField>
                <FormField
                  type="text"
                  name="first_name"
                  label={t('admin.users.firstName')}
                  value={formData.first_name ?? ''}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  disabled={isSubmitting}
                />
                <FormField
                  type="text"
                  name="last_name"
                  label={t('admin.users.lastName')}
                  value={formData.last_name ?? ''}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  disabled={isSubmitting}
                />
                <FormField
                  type="tel"
                  name="phone"
                  label={t('admin.users.phone')}
                  value={formData.phone ?? ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={isSubmitting}
                />
                <FormField
                  type="text"
                  name="address"
                  label={t('admin.users.address')}
                  value={formData.address ?? ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  disabled={isSubmitting}
                />
                <FormField
                  type="password"
                  name="password"
                  label={editingUser ? t('admin.users.newPassword') : t('admin.users.password')}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={formErrors.password}
                  required={!editingUser}
                  disabled={isSubmitting}
                  helperText={editingUser ? t('admin.users.passwordOptional') : t('admin.users.passwordHelp')}
                />
              </div>
              <div className="user-management-page__form-actions">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {t('common.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Users List */}
        <Card variant="elevated" padding="lg" className="user-management-page__users-card">
          <h3>{t('admin.users.usersList')} ({filteredUsers.length})</h3>
          {filteredUsers.length === 0 ? (
            <p className="user-management-page__empty">{t('admin.users.noUsers')}</p>
          ) : (
            <div className="user-management-page__users-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('admin.users.email')}</th>
                    <th>{t('admin.users.username')}</th>
                    <th>{t('admin.users.role')}</th>
                    <th>{t('admin.users.name')}</th>
                    <th>{t('admin.users.event')}</th>
                    <th>{t('admin.users.createdAt')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>{user.username}</td>
                      <td>
                        <span className={`user-management-page__role-badge user-management-page__role-badge--${user.role}`}>
                          {t(`admin.users.roles.${user.role}`)}
                        </span>
                      </td>
                      <td>{user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '-'}</td>
                      <td>{user.event_id ? events.find(e => e.id === user.event_id)?.name || user.event_id : '-'}</td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="user-management-page__actions-cell">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            title={t('common.edit')}
                          >
                            <Icon name="editar" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            title={t('common.delete')}
                            className="user-management-page__delete-btn"
                          >
                            <Icon name="basurero" size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;

